/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Typography, message, Alert, Input } from 'antd';
import { CopyOutlined, LoginOutlined } from '@ant-design/icons';
import { isMobileDevice } from '@/utils/device';
import { getInviterByShareCode } from '@/services/user';
import { UserDTO } from '@/types';

const { Title, Paragraph } = Typography;

const Invite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const shareCode = searchParams.get('shareCode') || '';
  const [inviter, setInviter] = useState<UserDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [shareCodeValid, setShareCodeValid] = useState(true);
  const [rewardPoints, setRewardPoints] = useState<number>(0);

  // 判断设备类型
  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(isMobileDevice());
    };
    checkDevice();
    // 监听窗口大小变化
    window.addEventListener('resize', checkDevice);
    return () => {
      window.removeEventListener('resize', checkDevice);
    };
  }, []);

  // 获取数据
  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        if (shareCode) {
          const inviterInfo = await getInviterByShareCode(shareCode);
          if (!inviterInfo) {
            setShareCodeValid(false);
          } else {
            setInviter(inviterInfo);
          }
        } else {
          setShareCodeValid(false);
        }
        setRewardPoints(window.config?.rewardPoints || 0);
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    };

    init();
  }, [shareCode]);

  // 处理登录跳转
  const handleLogin = () => {
    const loginUrl = shareCode ? `/login?shareCode=${encodeURIComponent(shareCode)}` : '/login';
    navigate(loginUrl);
  };

  // 处理复制邀请链接
  const handleCopyLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      message.success('邀请链接已复制到剪贴板');
    } catch (e) {
      // 降级方案：使用传统方法
      const textArea = document.createElement('textarea');
      textArea.value = currentUrl;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('邀请链接已复制到剪贴板');
      } catch (err) {
        message.error('复制失败，请手动复制浏览器地址栏中的链接');
      } finally {
        document.body.removeChild(textArea);
      }
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-64px)] lg:min-h-[calc(100vh-80px)] overflow-hidden">
      {/* 分享码无效提示 */}
      {!shareCodeValid && (
        <Alert
          banner
          description="您访问的邀请链接中的分享码不存在或不正确，您可以继续使用，但可能影响到积分获取。"
          type="warning"
          className="fixed top-16 lg:top-20 left-0 right-0 p-2 z-[1]"
        />
      )}
      <Card
        size="small"
        className="shadow-xl z-[1]"
        style={{
          width: isMobile ? '80%' : '500px',
          margin: '22rem auto',
        }}
        styles={{
          body: {
            padding: isMobile ? '24px 16px' : '40px',
          },
        }}
      >
        {/* 标题 */}
        <div className="text-center mb-6">
          <Title level={2} className="!mb-4 !text-3xl md:!text-4xl">
            小灵面试
          </Title>
          <div className="h-1 w-20 bg-gradient-to-r from-indigo-500 to-violet-500 mx-auto rounded-full"></div>
        </div>

        {/* 邀请文案 */}
        <div className="text-center mb-6">
          <Paragraph className="!mb-0 text-base md:text-lg text-gray-700 leading-relaxed">
            {loading ? (
              '加载中...'
            ) : (
              <>
                {inviter?.username && (
                  <span className="mr-1 text-lg font-extrabold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
                    {inviter?.username}
                  </span>
                )}
                正在邀请您登录小灵面试，
                {isMobile ? '请复制邀请链接，并使用 PC 打开体验' : '请点击下方登录按钮进行体验'}
                <span
                  className="ml-1 text-base md:text-lg font-extrabold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent"
                  style={{ WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}
                >
                  小灵面试助您轻松找工作
                </span>
              </>
            )}
          </Paragraph>
        </div>

        {/* 奖励信息 */}
        <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 md:p-6 mb-6">
          <div className="text-center">
            <div className="text-lg md:text-xl font-bold text-amber-700 mb-2">🎁 邀请奖励</div>
            <div className="text-sm md:text-base text-amber-800">
              新用户登录之后，您和邀请者都将获得
              <span className="mx-1 text-lg font-extrabold bg-gradient-to-r from-indigo-500 to-violet-500 bg-clip-text text-transparent">
                {rewardPoints > 0 ? rewardPoints : ''}
              </span>
              积分奖励
            </div>
          </div>
        </div>

        {/* 操作按钮 */}
        <div className="flex flex-col gap-6">
          {isMobile ? (
            <>
              <Input disabled value={window.location.href} />
              <Button
                type="primary"
                size="large"
                icon={<CopyOutlined />}
                onClick={handleCopyLink}
                className="h-12 text-base font-medium"
                loading={loading}
              >
                复制邀请链接
              </Button>
            </>
          ) : (
            <Button
              type="primary"
              size="large"
              icon={<LoginOutlined />}
              onClick={handleLogin}
              className="h-12 text-base font-medium"
              loading={loading}
            >
              立即登录
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Invite;
